/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import { Map } from 'immutable';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import { green } from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
const DEFAULT_DATE_FORMAT_OPTIONS = {
  weekday: 'long',
  month:   'long',
  day:     'numeric',
  year:    'numeric',
};

/* ––
 * ––– Component
 * –––––––––––––––––––––––––––––––– */
function AppointmentCard({ appointment, onDiscard, disableReschedule = false, onAutoSubmit = null }) {
  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  const formattedAppointmentDate = useMemo(() => {
    const formattedDate = appointment.get('startTime').toLocaleString(DEFAULT_DATE_FORMAT_OPTIONS);
    const formattedStartTime = appointment.get('startTime').toLocaleString(DateTime.TIME_SIMPLE);
    const formattedEndTime = appointment.get('endTime').toLocaleString(DateTime.TIME_SIMPLE);

    return `${formattedDate} ${formattedStartTime} - ${formattedEndTime} (${appointment.get('startTime').offsetNameShort})`;
  }, [appointment]);

  useEffect(() => {
    if (!disableReschedule || !onAutoSubmit) return;

    const timeoutId = setTimeout(() => { onAutoSubmit(); }, 1000);

    return () => clearTimeout(timeoutId);
  }, [disableReschedule, onAutoSubmit]);

  return (
    <Box sx={{
      pt: 4, px: { xs: 2, sm: 4 }, pb: 10, position: 'relative',
    }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{ mx: 'auto', width: { xs: '100%', sm: '70%' } }}
      >
        <CheckIcon
          color="success"
          sx={{
            fontSize: 48, p: 1, borderRadius: '50%', backgroundColor: green.A100,
          }}
        />
        <Typography variant="h3" sx={{ mt: 2.5, fontWeight: 'medium' }}>{t('eos.landing_page.text.booking.appointment_confirmation.title')}</Typography>
        <Typography variant="body" textAlign="center" sx={{ mt: 1 }}>{t('eos.landing_page.text.booking.appointment_confirmation.description')}</Typography>

        <Divider
          sx={{
            my: 3, width: '100%', borderWidth: 1, borderColor: 'grey.300',
          }}
        />

        <Box display="flex" justifyContent="space-between" sx={{ width: '100%', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          <Typography variant="body" sx={{ fontWeight: 'medium', width: { xs: '100%', sm: 'auto' } }}>
            {t('eos.landing_page.label.booking.appointment_date')}
          </Typography>
          <Typography
            variant="body"
            sx={{
              maxWidth: { xs: '100%', sm: '50%' },
              width:    { xs: '100%', sm: 'auto' },
              mt:       { xs: 0.5, sm: 0 },
            }}
          >
            {formattedAppointmentDate}
          </Typography>
        </Box>

        {appointment.get('location') && (
          <Box display="flex" justifyContent="space-between" sx={{ mt: { xs: 2.25, sm: 3 }, width: '100%', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <Typography variant="body" sx={{ fontWeight: 'medium', width: { xs: '100%', sm: 'auto' } }}>
              {t('eos.landing_page.label.booking.appointment_location')}
            </Typography>
            <Typography
              variant="body"
              sx={{
                maxWidth: { xs: '100%', sm: '50%' },
                width:    { xs: '100%', sm: 'auto' },
                mt:       { xs: 0.5, sm: 0 },
              }}
            >
              {appointment.get('location')}
            </Typography>
          </Box>
        )}
      </Box>

      {!disableReschedule && (
        <Box sx={{
          position:        'absolute',
          bottom:          0,
          left:            0,
          px:              4,
          width:           '100%',
          py:              1.5,
          backgroundColor: 'background.dark',
          textAlign:       'center',
        }}
        >
          <Typography variant="body">
            {t('eos.landing_page.text.booking.appointment_confirmation.reschedule')}
            <Typography
              variant="body"
              component="span"
              sx={{
                display:                 'inline-block',
                ml:                      1,
                cursor:                  'pointer',
                color:                   'text.secondary',
                textDecoration:          'underline',
                textUnderlinePosition:   'from-font',
                textDecorationThickness: 'from-font',
              }}
              onClick={onDiscard}
            >
              {t('eos.landing_page.label.booking.reschedule')}
            </Typography>
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/* ––
 * ––– Props declaration
 * –––––––––––––––––––––––––––––––– */
AppointmentCard.propTypes = {
  appointment:       PropTypes.instanceOf(Map).isRequired,
  onDiscard:         PropTypes.func.isRequired,
  disableReschedule: PropTypes.bool,
  onAutoSubmit:      PropTypes.func,
};

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default AppointmentCard;
